import React from 'react';
import { Typography, CircularProgress } from '@mui/material';
import type { CircularProgressProps } from '@mui/material';
import { Box, styled } from '@mui/system';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _light = clientThemePalette[CLIENT_THEME].light;
const _dark = clientThemePalette[CLIENT_THEME].dark;

interface BackgroundCircleProps extends CircularProgressProps {
  $enableDarkTheme: boolean;
}

interface ContentBoxProps {
  $enableDarkTheme: boolean;
}

interface Props {
  enableDarkTheme: boolean;
  target: number;
  value: number;
}

const StyledCircularProgress = styled(CircularProgress)(() => ({
  color: _light.primary,
  '& .MuiCircularProgress-circle': {
    strokeLinecap: 'round',
  },
}));

const BackgroundCircle = styled(CircularProgress)<BackgroundCircleProps>(
  ({ $enableDarkTheme }) => ({
    color: $enableDarkTheme ? _dark.toolBarBackground : '#E7EBEF',
  })
);

const ContentBox = styled(Box)<ContentBoxProps>(({ $enableDarkTheme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  color: $enableDarkTheme ? _dark.textSecondary : _light.textSecondary,
}));

const ProgressWrapper = styled(Box)({
  transform: 'rotate(-180deg)',
  borderRadius: '50%',
});

const CircularProgressChart: React.FC<Props> = ({
  enableDarkTheme,
  target,
  value,
}) => {
  const { t } = useTranslation();
  let percentageValue = target ? (value / target) * 100 : 0;

  if (percentageValue > 100) {
    percentageValue = 100;
  }

  return (
    <Box
      position="relative"
      display="inline-flex"
      sx={{
        borderRadius: '50%',
        padding: '5px',
      }}
    >
      <ProgressWrapper>
        <BackgroundCircle
          variant="determinate"
          value={100}
          size={150}
          thickness={3}
          $enableDarkTheme={enableDarkTheme}
        />
        <StyledCircularProgress
          variant="determinate"
          value={percentageValue}
          size={150}
          thickness={3}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
          }}
        />
      </ProgressWrapper>
      <ContentBox $enableDarkTheme={enableDarkTheme}>
        <Typography
          component="div"
          color="inherit"
          sx={{
            fontSize: '12px',
          }}
        >
          {t('ytd_actual')}
        </Typography>
        <Typography
          component="div"
          color={_light.primary}
          sx={{
            fontSize: '24px',
            fontWeight: '600',
          }}
        >
          {formatNumberToShortScale(
            convertToTwoDecimalPlaces((value / 60).toString())
          )}
        </Typography>
        <Typography
          component="div"
          color="inherit"
          sx={{
            fontSize: '12px',
          }}
        >
          {t('hours')}
        </Typography>
      </ContentBox>
    </Box>
  );
};

export default CircularProgressChart;
